import { useQuery } from '@apollo/client';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import React, { FC, useContext, useEffect } from 'react';

import FlexColumn from '@ui/atoms/FlexColumn';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import { keysToCamel } from '@utils/butter';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';
import { IAccountInfoQueryData } from '@folx-types/member';

import ACCOUNT_INFO_QUERY from '@graphql/queries/AccountInfo.query.gql';
import MEMBER_INSURANCE_INFO_QUERY from '@graphql/queries/memberInsuranceInfo.query.gql';

import { IInsuranceDetailsStepContent } from './InsuranceDetailsStep.component';
import InsuranceDetailsStep from './InsuranceDetailsStep.component';

interface IInsuranceDetailsEditStepPageProps extends IFlowStepProps {
	memberInsuranceInfoId?: string;
}

const InsurancDetailsEditStepPage: FC<IInsuranceDetailsEditStepPageProps> = ({
	path,
	stepKey,
	memberInsuranceInfoId,
}) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "insurance-details-step" }) {
				title
				description
				tooltip
				tooltip_text
				first_name_label
				last_name_label
				member_id_label
				group_label
				is_policyholder_label
				policyholder_first_name_label
				policyholder_last_name_label
				policyholder_relationship_label
				policyholder_dob_label
				create_insurance_error
				update_insurance_error
			}
		}
	`);

	const { loading: accountLoading, data: accountData } =
		useQuery<IAccountInfoQueryData>(ACCOUNT_INFO_QUERY);

	const { loading: memberInsuranceInfoLoading, data: memberInsuranceInfoData } = useQuery(
		MEMBER_INSURANCE_INFO_QUERY,
		{
			variables: {
				id: memberInsuranceInfoId,
			},
			fetchPolicy: 'network-only',
		}
	);

	const member = accountData?.currentMember;

	const { handleFormSubmission, handleNavBack } = useContext(FlowContext);

	const refinedContent: IInsuranceDetailsStepContent = keysToCamel(
		pageContent
	) as IInsuranceDetailsStepContent;

	const submit = (id: string) => {
		navigate('../../insurance_selection');
	};

	const InsuranceSkeleton = () => (
		<CenteredGridItem>
			<FlexColumn gap="lgxl">
				<FlexColumn gap="sm">
					<Skeleton fullWidth height="lg" />
					<Skeleton fullWidth height="sm" />
				</FlexColumn>
				<FlexColumn gap="md">
					<FlexColumn gap="xs">
						<Skeleton fullWidth height="sm" />
						<Skeleton fullWidth height="lg" />
					</FlexColumn>
					<FlexColumn gap="xs">
						<Skeleton fullWidth height="sm" />
						<Skeleton fullWidth height="lg" />
					</FlexColumn>
					<FlexColumn gap="xs">
						<Skeleton fullWidth height="sm" />
						<Skeleton fullWidth height="lg" />
					</FlexColumn>
					<FlexColumn gap="xs">
						<Skeleton fullWidth height="sm" />
						<Skeleton fullWidth height="lg" />
					</FlexColumn>
				</FlexColumn>
			</FlexColumn>
		</CenteredGridItem>
	);

	return (
		<LoadingWrapper
			loading={accountLoading || memberInsuranceInfoLoading}
			skeleton={<InsuranceSkeleton />}>
			<InsuranceDetailsStep
				pageContent={refinedContent}
				member={member}
				path={path}
				stepKey={stepKey}
				handleFormSubmission={handleFormSubmission}
				handleNavBack={handleNavBack}
				selectedInsurance={memberInsuranceInfoData?.memberInsuranceInfo}
				submit={submit}
			/>
		</LoadingWrapper>
	);
};

export default InsurancDetailsEditStepPage;
